import UploadIcon from '@mui/icons-material/Upload'
import React, { ReactNode, useCallback, useRef, useState } from 'react'
import { Button, useNotify, useRefresh } from 'react-admin'
import { getUrl, httpClient } from '../../providers/data'

interface Props {
  certificateId: string
  renderAs?: (openFileDialog: () => void, loading: boolean) => ReactNode
  accept?: string
}

const CertificateUploadButton: React.FC<Props> = ({ certificateId, renderAs, accept }) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const openFileDialog = useCallback(() => fileInputRef.current?.click(), [])

  const onFileSelected = useCallback(async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0]
    if (!file) return
    setLoading(true)

    try {
      const form = new FormData()
      form.append('file', file)
      await httpClient(`${getUrl()}/certificate/${certificateId}/upload`, {
        method: 'POST',
        body: form,
      })
      notify('resources.certificates.actions.certificateUploaded', { type: 'info' })
      refresh()
    } catch (e) {
      console.error(e)
      // Show the server's (translated) reason, e.g. a certificate that does not belong to this CSR.
      notify((e as Error)?.message || 'ra.notification.http_error', { type: 'warning' })
    } finally {
      setLoading(false)
      // reset zodat dezelfde file opnieuw gekozen kan worden
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [certificateId, notify, refresh])

  if (renderAs) {
    return (
      <>
        {renderAs(openFileDialog, loading)}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept ?? '.pem,.crt,.cer,.p7b,.p7c,.der'}
          style={{ display: 'none' }}
          onChange={(e) => void onFileSelected(e)}
        />
      </>
    )
  }

  return (
    <>
      <Button
        label="resources.certificates.actions.uploadCertificate"
        variant="contained"
        startIcon={<UploadIcon />}
        onClick={openFileDialog}
        disabled={loading}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={accept ?? '.pem,.crt,.cer,.p7b,.p7c,.der'}
        style={{ display: 'none' }}
        onChange={(e) => void onFileSelected(e)}
      />
    </>
  )
}

export default CertificateUploadButton
